<template>
  <div data-testid="sso-hosting-mode-panel">
    <div class="subtitle-1 mb-2" data-testid="sso-hosting-mode-panel__title">Hosting mode</div>
    <v-radio-group
      v-model="localMode"
      :disabled="disabled || saving"
      class="mt-0"
      hide-details
      data-testid="sso-hosting-mode-panel__mode"
    >
      <v-radio
        :value="HOSTING_MODES.SHARED_APP_HOST"
        label="Shared app host (default) — users sign in on the Consark app host"
        data-testid="sso-hosting-mode-panel__shared"
      />
      <v-radio
        :value="HOSTING_MODES.DEDICATED_SUBDOMAIN"
        label="Dedicated subdomain — tenant login host (e.g. acme.consarc.ai)"
        data-testid="sso-hosting-mode-panel__dedicated"
      />
    </v-radio-group>

    <v-alert
      v-if="localMode === HOSTING_MODES.SHARED_APP_HOST && !isAppHostSsoAdminClient"
      type="info"
      text
      dense
      border="left"
      class="mt-3 mb-0"
    >
      Login stays on the app host. Auth policy and shared IdPs are managed under
      Consark Development (platform SSO admin client).
    </v-alert>

    <v-expand-transition>
      <div v-if="localMode === HOSTING_MODES.DEDICATED_SUBDOMAIN" class="mt-3">
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="bootstrapHostname"
              label="Primary hostname*"
              outlined
              dense
              hide-details="auto"
              hint="e.g. acme.consarc.ai"
              persistent-hint
              :disabled="disabled || saving || hasDedicatedDomain"
              data-testid="sso-hosting-mode-panel__hostname"
            />
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="bootstrapSlug"
              label="Slug*"
              outlined
              dense
              hide-details="auto"
              hint="DNS label, unique among active hosts"
              persistent-hint
              :disabled="disabled || saving || hasDedicatedDomain"
              data-testid="sso-hosting-mode-panel__slug"
            />
          </v-col>
        </v-row>
      </div>
    </v-expand-transition>

    <div class="mt-4 d-flex align-center">
      <v-btn
        color="primary"
        class="primaryBtn"
        :loading="saving"
        :disabled="disabled || !dirty || !canSave"
        data-testid="sso-hosting-mode-panel__save"
        @click="save"
      >
        Save hosting mode
      </v-btn>
      <span v-if="currentMode" class="ml-3 caption grey--text">
        Current: {{ currentModeLabel }}
      </span>
    </div>
  </div>
</template>

<script>
import constants from '@/plugins/constants';

const { SSO } = constants;
const HOSTING_MODES = SSO.HOSTING_MODES;

export default {
  name: 'HostingModePanel',
  props: {
    currentMode: { type: String, default: '' },
    isAppHostSsoAdminClient: { type: Boolean, default: false },
    hasDedicatedDomain: { type: Boolean, default: false },
    existingHostname: { type: String, default: '' },
    existingSlug: { type: String, default: '' },
    saving: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      HOSTING_MODES,
      localMode: this.currentMode || HOSTING_MODES.SHARED_APP_HOST,
      bootstrapHostname: this.existingHostname || '',
      bootstrapSlug: this.existingSlug || '',
    };
  },
  computed: {
    dirty() {
      const modeChanged = this.localMode !== (this.currentMode || HOSTING_MODES.SHARED_APP_HOST);
      if (this.localMode === HOSTING_MODES.DEDICATED_SUBDOMAIN && !this.hasDedicatedDomain) {
        return true;
      }
      return modeChanged;
    },
    canSave() {
      if (this.localMode === HOSTING_MODES.SHARED_APP_HOST) return true;
      if (this.hasDedicatedDomain) return true;
      return Boolean(
        String(this.bootstrapHostname || '').trim() &&
          String(this.bootstrapSlug || '').trim(),
      );
    },
    currentModeLabel() {
      if (this.currentMode === HOSTING_MODES.DEDICATED_SUBDOMAIN) return 'Dedicated subdomain';
      if (this.currentMode === HOSTING_MODES.SHARED_APP_HOST) return 'Shared app host';
      return 'Not configured';
    },
  },
  watch: {
    currentMode(mode) {
      this.localMode = mode || HOSTING_MODES.SHARED_APP_HOST;
    },
    existingHostname(value) {
      if (value) this.bootstrapHostname = value;
    },
    existingSlug(value) {
      if (value) this.bootstrapSlug = value;
    },
    bootstrapHostname(value) {
      if (!this.bootstrapSlug && value) {
        const label = String(value).split('.')[0] || '';
        this.bootstrapSlug = label.toLowerCase().replace(/[^a-z0-9-]/g, '');
      }
    },
  },
  methods: {
    save() {
      this.$emit('save', {
        hostingMode: this.localMode,
        hostname: String(this.bootstrapHostname || '').trim().toLowerCase(),
        slug: String(this.bootstrapSlug || '').trim().toLowerCase(),
      });
    },
  },
};
</script>
